import React, { useState, useEffect, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api/index.js';
import { useTenantExportBranding } from '../hooks/useTenantExportBranding.js';
import { useNotification } from '../context/NotificationContext.jsx';
import {
    Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Stack, Card, Chip, IconButton, Divider, Tooltip, CircularProgress,
    Alert, Grid, Paper, Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
    Add as AddIcon,
    MoreVert as MoreVertIcon,
    DeleteOutline as DeleteIcon,
    Block as RevokeIcon,
    ContentCopy as CopyIcon,
    CheckCircle as ActiveIcon,
    RadioButtonUnchecked as PendingIcon,
    WifiOff as OfflineIcon,
    Refresh as RefreshIcon,
    Terminal as TerminalIcon,
    VpnKey as KeyIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { LoadingSpinner, ConfirmModal } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { formatRelativeTime } from '../utils/dateTime';


const STATUS_META = {
    ACTIVE: { label: 'Active', color: 'success', Icon: ActiveIcon },
    PENDING: { label: 'Pending', color: 'warning', Icon: PendingIcon },
    OFFLINE: { label: 'Offline', color: 'default', Icon: OfflineIcon },
    IDLE: { label: 'Idle', color: 'default', Icon: OfflineIcon },
    REVOKED: { label: 'Revoked', color: 'error', Icon: RevokeIcon },
};

// ─────────────────────────────────────────────────────────────────────────────
// Token Display Dialog
// ─────────────────────────────────────────────────────────────────────────────
const TokenDialog = ({ open, agentId, agentName, token: regToken, onClose, onRegenToken }) => {
    const [copied, setCopied] = useState(false);
    const [deployTab, setDeployTab] = useState('run');           // 'run' | 'compose'
    const [persistStorage, setPersistStorage] = useState(false);

    // Auto-generate unique container name from agentId (always available, never conflicts)
    // agentName is used as a readable prefix only
    const shortId = (agentId || '').slice(0, 8);  // first 8 chars of UUID — always unique
    const nameSlug = (agentName || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    // e.g. "aidas-relay-hq-on-prem-agent-a1b2c3d4" or "aidas-relay-a1b2c3d4" if no name
    const containerName = nameSlug
        ? `aidas-relay-${nameSlug}-${shortId}`
        : `aidas-relay-${shortId}`;

    // Auto-detect user's OS for a sensible default host path
    const detectedOS = useMemo(() => {
        const ua = (navigator.userAgent || '').toLowerCase();
        const plat = (navigator.platform || '').toLowerCase();
        if (plat.startsWith('win') || ua.includes('windows')) return 'windows';
        if (plat.startsWith('mac') || ua.includes('macintosh')) return 'mac';
        return 'linux';
    }, []);
    const defaultHostPath = useMemo(() => {
        if (detectedOS === 'windows') return `C:/aidas/relay-agent/${shortId}`;
        if (detectedOS === 'mac') return `/Users/Shared/aidas/relay-agent/${shortId}`;
        return `/opt/aidas/relay-agent/${shortId}`;
    }, [detectedOS, shortId]);

    const [hostPath, setHostPath] = useState(defaultHostPath);
    const tenantBranding = useTenantExportBranding();

    // Smart Platform URL from env — auto-fix localhost for Docker
    const rawBase = (import.meta.env.VITE_API_BASE_URL || '');
    const stripped = rawBase.replace(/\/api\/v1\/?$/, '').replace(/\/+$/, '') || window.location.origin;
    const dockerDefault = stripped
        .replace(/localhost/gi, 'host.docker.internal')
        .replace(/127\.0\.0\.1/gi, 'host.docker.internal');
    const [platformUrl, setPlatformUrl] = useState(dockerDefault);
    const isLocalhost = platformUrl.includes('localhost') || platformUrl.includes('127.0.0.1');

    const tokenStr = regToken || '';
    const hasToken = Boolean(regToken);

    // Volume strings — Docker always uses forward slashes for volume mounts,
    // even on Windows (Docker Desktop translates automatically).
    // Convert any backslashes to forward slashes to avoid "invalid mode" errors.
    const dockerPath = hostPath.replace(/\\/g, '/').replace(/\/+$/, '');
    const dataVol = persistStorage ? `${dockerPath}/data:/data` : `relay_data_${shortId}:/data`;
    const regVol = persistStorage ? `${dockerPath}/registry:/data/registry` : `relay_registry_${shortId}:/data/registry`;

    const ecrImage = 'public.ecr.aws/f6d2y2r7/aidasapps/relay-agent:latest';
    // const ecrImage = 'public.ecr.aws/s5s5d9o6/aidasapps/relay-agent:latest';

    // docker pull command (step 0)
    const dockerPullCmd = `docker pull ${ecrImage}`;

    // docker run command — container name is unique per agent (fully automatic)
    const dockerRunParts = [
        'docker run -d',
        `--name ${containerName}`,
        '--restart unless-stopped',
        `-e PLATFORM_URL=${platformUrl}`,
        `-e AGENT_TOKEN=${tokenStr}`,
        `-v ${dataVol}`,
        `-v ${regVol}`,
        ecrImage,
    ];
    // Display version: multi-line with backslashes for readability
    const dockerRunCmdDisplay = dockerRunParts.map((p, i) =>
        i < dockerRunParts.length - 1 ? `  ${i === 0 ? p : p} \\` : `  ${p}`
    ).join('\n').replace('  docker', 'docker');
    // Copy version: single line — works on any OS without backslash issues
    const dockerRunCmdCopy = dockerRunParts.join(' ');

    // docker-compose.yml — container name matches docker run
    const composeLines = [
        'version: "3.9"',
        'services:',
        `  ${containerName}:`,
        `    image: ${ecrImage}`,
        `    container_name: ${containerName}`,
        '    restart: unless-stopped',
        '    environment:',
        `      - PLATFORM_URL=${platformUrl}`,
        `      - AGENT_TOKEN=${tokenStr}`,
        '    volumes:',
        `      - ${dataVol}`,
        `      - ${regVol}`,
        '    network_mode: host',
    ];
    if (!persistStorage) {
        composeLines.push('', 'volumes:', `  relay_data_${shortId}:`, `  relay_registry_${shortId}:`);
    }
    const composeCmd = [
        `# Portal: ${tenantBranding.portalName || 'Platform'}`,
        tenantBranding.watermark?.text ? `# Watermark: ${tenantBranding.watermark.text}` : null,
        `# Exported At: ${dayjs().toISOString()}`,
        '',
        ...composeLines
    ].filter(Boolean).join('\n');

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleDownloadCompose = () => {
        const blob = new Blob([composeCmd], { type: 'text/yaml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'docker-compose.yml';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <DynamicIcon name="ShieldCheck" className="w-5 h-5 text-green-500" />
                    <span>{hasToken ? 'Agent Created — Save Your Token' : 'Agent Setup Instructions'}</span>
                </Stack>
            </DialogTitle>
            <DialogContent>
                {hasToken ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This registration token is shown <strong>only once</strong>. Save it now.
                    </Alert>
                ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        The original token is no longer available. Use <strong>Regenerate Token</strong> from the agent menu to get a new one, then run the command below.
                    </Alert>
                )}

                {/* Registration Token */}
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Registration Token
                </Typography>
                <Box sx={{
                    fontFamily: 'monospace', fontSize: '0.8rem', bgcolor: 'action.hover',
                    borderRadius: 1, p: 1.5, mb: 2, wordBreak: 'break-all',
                    border: '1px solid', borderColor: hasToken ? 'success.light' : 'divider',
                    position: 'relative',
                }}>
                    {hasToken ? regToken : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Token not available — regenerate to get a new one</span>}
                    <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: 4, right: 4 }}>
                        {/* Inline regenerate button always visible */}
                        {onRegenToken && (
                            <Tooltip title="Regenerate Token">
                                <IconButton size="small" onClick={onRegenToken} color="primary">
                                    <KeyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {hasToken && (
                            <Tooltip title={copied ? 'Copied!' : 'Copy token'}>
                                <IconButton size="small" onClick={() => handleCopy(regToken)}>
                                    <CopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                </Box>

                {/* Platform URL */}
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Platform URL
                    <Typography component="span" variant="caption" color="text.disabled"> (edit if needed)</Typography>
                </Typography>
                <TextField fullWidth size="small" value={platformUrl}
                    onChange={(e) => setPlatformUrl(e.target.value)}
                    sx={{ mb: 1 }}
                    inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                />
                {isLocalhost && (
                    <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
                        Running in Docker? Replace <code>localhost</code> with <code>host.docker.internal</code>.
                    </Alert>
                )}

                {/* Options */}
                <Box sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                    p: 1.5, mb: 2, bgcolor: 'action.hover',
                }}>
                    <Stack direction="row" alignItems="center" spacing={1}
                        onClick={() => setPersistStorage(!persistStorage)}
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                        <input type="checkbox" checked={persistStorage}
                            onChange={() => setPersistStorage(!persistStorage)}
                            style={{ accentColor: '#2563eb', width: 16, height: 16 }}
                        />
                        <Typography variant="body2" fontWeight={600}>
                            Persistent storage on host machine
                        </Typography>
                    </Stack>
                    {persistStorage && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, ml: 3.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                📁 Host path:
                            </Typography>
                            <TextField size="small" fullWidth value={hostPath}
                                onChange={(e) => setHostPath(e.target.value)}
                                placeholder={defaultHostPath}
                                inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem', padding: '4px 8px' } }}
                                sx={{
                                    '& .MuiOutlinedInput-root': { height: 28, bgcolor: '#fff' },
                                }}
                            />
                        </Stack>
                    )}
                </Box>

                {/* Pull Command */}
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }} display="block">
                    Step 1 — Pull Image from AWS ECR
                </Typography>
                <Box sx={{
                    fontFamily: 'monospace', fontSize: '0.72rem', bgcolor: 'grey.900',
                    color: '#86efac', borderRadius: 1.5, p: 1.5, mb: 1.5,
                    whiteSpace: 'pre', overflow: 'auto',
                    border: '1px solid', borderColor: 'grey.800',
                    position: 'relative',
                }}>
                    {dockerPullCmd}
                    <Tooltip title="Copy pull command">
                        <IconButton
                            size="small"
                            onClick={() => handleCopy(dockerPullCmd)}
                            sx={{ position: 'absolute', top: 4, right: 4, color: 'grey.400' }}
                        >
                            <CopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Deploy Command Header */}
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1 }} display="block">
                    Step 2 — Deploy Command
                </Typography>

                {/* Tab Switcher */}
                <Stack direction="row" spacing={0} alignItems="flex-end" sx={{ mb: 0 }}>
                    {[
                        { key: 'run', label: 'docker run' },
                        { key: 'compose', label: 'docker-compose.yml' },
                    ].map(tab => (
                        <Box
                            key={tab.key}
                            onClick={() => setDeployTab(tab.key)}
                            sx={{
                                px: 2, py: 0.75, cursor: 'pointer',
                                fontSize: '0.75rem', fontWeight: 600, fontFamily: 'monospace',
                                borderRadius: '6px 6px 0 0',
                                bgcolor: deployTab === tab.key ? 'grey.900' : 'action.hover',
                                color: deployTab === tab.key ? 'grey.100' : 'text.secondary',
                                transition: 'all 0.15s ease',
                                border: '1px solid',
                                borderColor: deployTab === tab.key ? 'grey.900' : 'divider',
                                borderBottom: deployTab === tab.key ? 'none' : undefined,
                            }}
                        >
                            {tab.label}
                        </Box>
                    ))}

                </Stack>

                {/* Command Block */}
                <Box sx={{
                    fontFamily: 'monospace', fontSize: '0.72rem', bgcolor: 'grey.900',
                    color: 'grey.100', borderRadius: '0 4px 4px 4px', p: 1.5,
                    whiteSpace: 'pre', overflow: 'auto', maxHeight: 260,
                    position: 'relative', lineHeight: 1.7,
                    border: '1px solid', borderColor: 'grey.800',
                }}>
                    {deployTab === 'run' ? dockerRunCmdDisplay : composeCmd}
                </Box>

                {/* Action buttons + Run instructions */}
                {deployTab === 'run' ? (
                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                        <Button
                            variant="outlined" size="small" startIcon={<CopyIcon fontSize="small" />}
                            onClick={() => handleCopy(dockerRunCmdCopy)}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '0.78rem' }}
                        >
                            {copied ? 'Copied!' : 'Copy Command'}
                        </Button>
                        <Alert severity="info" sx={{ fontSize: '0.73rem' }} icon={false}>
                            <strong>Run instructions:</strong> Paste the copied command in your terminal to start the agent.
                        </Alert>
                    </Stack>
                ) : (
                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                        <Button
                            variant="outlined" size="small"
                            startIcon={<DynamicIcon name="Download" className="w-4 h-4" />}
                            onClick={handleDownloadCompose}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '0.78rem' }}
                        >
                            Download docker-compose.yml
                        </Button>
                        <Alert severity="info" sx={{ fontSize: '0.73rem' }} icon={false}>
                            <strong>Run instructions:</strong> Save the downloaded file to your project folder, then run:
                            <Box component="code" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 600 }}>
                                docker compose up -d
                            </Box>
                        </Alert>
                    </Stack>
                )}

                {/* Agent ID */}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }} display="block">
                    Agent ID: <code>{agentId}</code>
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} variant="contained">
                    {hasToken ? "I've saved the token" : 'Close'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};


// ─────────────────────────────────────────────────────────────────────────────
// Agent Card
// ─────────────────────────────────────────────────────────────────────────────
const AgentCard = ({ agent, onRevoke, onDelete, onShowSetup, onRegenToken, theme }) => {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const meta = STATUS_META[agent.status] || STATUS_META.OFFLINE;
    const { Icon: StatusIcon } = meta;

    return (
        <Card elevation={0} sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: 2,
            transition: 'all 0.18s ease',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[4], borderColor: 'primary.main' },
        }}>
            <Box sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={{
                        width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
                        bgcolor: agent.status === 'ACTIVE' ? 'success.light' : 'action.hover',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <DynamicIcon name="Server" className="w-5 h-5" style={{
                            color: agent.status === 'ACTIVE' ? '#16a34a' : '#94a3b8'
                        }} />
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap>{agent.name}</Typography>
                        {agent.description && (
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                                {agent.description}
                            </Typography>
                        )}
                    </Box>
                    <IconButton size="small"
                        onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}>
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Chip
                        icon={<StatusIcon sx={{ fontSize: '14px !important' }} />}
                        label={meta.label} size="small" color={meta.color} variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                    <Stack direction="row" spacing={2}>
                        <Typography variant="caption" color="text.secondary">v{agent.version || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">♥ {formatRelativeTime(agent.heartbeat_at)}</Typography>
                    </Stack>
                </Stack>
            </Box>

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                {/* View Setup */}
                <MenuItem onClick={() => { setMenuAnchor(null); onShowSetup(agent); }}>
                    <ListItemIcon><TerminalIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>View Setup Command</ListItemText>
                </MenuItem>
                <Divider />
                {agent.status !== 'REVOKED' && (
                    <MenuItem onClick={() => { setMenuAnchor(null); onRevoke(agent); }} sx={{ color: 'warning.main' }}>
                        <ListItemIcon><RevokeIcon fontSize="small" color="warning" /></ListItemIcon>
                        <ListItemText>Revoke Agent</ListItemText>
                    </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={() => { setMenuAnchor(null); onDelete(agent); }} sx={{ color: 'error.main' }}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>
        </Card>
    );
};


// ─────────────────────────────────────────────────────────────────────────────
// Create Agent Dialog
// ─────────────────────────────────────────────────────────────────────────────
const CreateAgentDialog = ({ open, onClose, onCreated, token }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const { addNotification } = useNotification();

    const handleClose = () => { setName(''); setDescription(''); onClose(); };

    const handleCreate = async () => {
        if (!name.trim()) return;
        setSaving(true);
        try {
            const result = await apiFetch('/relay/agents', token, {
                method: 'POST',
                body: JSON.stringify({ name: name.trim(), description: description.trim() || null }),
            });
            onCreated(result);
            handleClose();
        } catch (e) {
            addNotification(e.message || 'Failed to create agent.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>Register New Hybrid Agent</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <TextField
                        label="Agent Name *" size="small" fullWidth
                        value={name} onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. HQ On-Prem Agent"
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                    />
                    <TextField
                        label="Description" size="small" fullWidth multiline rows={2}
                        value={description} onChange={(e) => setDescription(e.target.value)}
                        placeholder="Optional — what database does this agent connect to?"
                    />
                    <Alert severity="info" sx={{ fontSize: '0.78rem' }}>
                        After creating, you'll receive a one-time registration token to pass to the Docker container.
                    </Alert>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button variant="contained" onClick={handleCreate}
                    disabled={saving || !name.trim()}
                    startIcon={saving ? <CircularProgress size={14} /> : null}>
                    Create Agent
                </Button>
            </DialogActions>
        </Dialog>
    );
};


// ─────────────────────────────────────────────────────────────────────────────
// Main Component — Agent Management (single view, no tabs)
// ─────────────────────────────────────────────────────────────────────────────
const RelayAgentsSettings = ({ tenantId, token }) => {
    const theme = useTheme();
    const { addNotification } = useNotification();

    const { data: agents, isLoading, refresh } = useData(
        tenantId ? `/relay/agents?tenant_id=${tenantId}` : '/relay/agents'
    );

    const [createOpen, setCreateOpen] = useState(false);
    const [tokenDialog, setTokenDialog] = useState(null);
    const [confirmRevoke, setConfirmRevoke] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [confirmRegen, setConfirmRegen] = useState(null);
    const [regenLoading, setRegenLoading] = useState(false);

    const handleCreated = (result) => {
        // Build a minimal agent stub so the inline Regenerate key button works immediately
        const agentStub = { id: result.agent_id, name: 'New Agent' };
        setTokenDialog({ agentId: result.agent_id, regToken: result.reg_token, regenFor: agentStub });
        refresh();
    };

    // Show setup dialog for existing agents — does NOT regenerate token automatically
    // because regeneration resets the agent to PENDING status, killing the running container.
    const handleShowSetup = (agent) => {
        setTokenDialog({ agentId: agent.id, regToken: null, regenFor: agent });
    };

    // Regenerate token — WARNING: this resets agent status to PENDING, invalidating the running container.
    // Step 1: Show in-app confirmation dialog
    const handleRegenToken = (agent) => {
        if (!agent?.id) {
            addNotification('Cannot regenerate: agent info missing.', 'error');
            return;
        }
        setConfirmRegen(agent);
    };

    // Step 2: Actually perform the regeneration after user confirms
    const executeRegenToken = async () => {
        if (!confirmRegen) return;
        const agent = confirmRegen;
        setConfirmRegen(null);
        setRegenLoading(true);
        try {
            const result = await apiFetch(`/relay/agents/${agent.id}/regenerate-token`, token, { method: 'POST' });
            setTokenDialog(prev => ({
                ...prev,
                agentId: result.agent_id,
                regToken: result.reg_token,
                regenFor: agent,
            }));
            addNotification('Token regenerated. The agent must be restarted with the new token.', 'warning');
            refresh();
        } catch (e) {
            addNotification(e.message || 'Failed to regenerate token.', 'error');
        } finally {
            setRegenLoading(false);
        }
    };

    // Open the token dialog for an existing agent (no token)
    // The inline key button inside TokenDialog will call handleRegenToken
    const handleOpenSetupForAgent = (agent) => {
        setTokenDialog({ agentId: agent.id, regToken: null, regenFor: agent });
    };

    const handleRevoke = async () => {
        if (!confirmRevoke) return;
        try {
            await apiFetch(`/relay/agents/${confirmRevoke.id}/revoke`, token, { method: 'PATCH' });
            addNotification(`Agent "${confirmRevoke.name}" revoked.`, 'success');
            refresh();
        } catch (e) {
            addNotification(e.message || 'Failed to revoke.', 'error');
        } finally {
            setConfirmRevoke(null);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await apiFetch(`/relay/agents/${confirmDelete.id}`, token, { method: 'DELETE' });
            addNotification(`Agent "${confirmDelete.name}" deleted.`, 'success');
            refresh();
        } catch (e) {
            addNotification(e.message || 'Failed to delete.', 'error');
        } finally {
            setConfirmDelete(null);
        }
    };

    const agentList = agents || [];
    const activeCount = agentList.filter(a => a.status === 'ACTIVE').length;

    return (
        <Box sx={{ maxWidth: 1100, mx: 'auto', py: 2 }}>

            {/* ── Page Header ── */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <DynamicIcon name="Server" className="w-5 h-5 text-indigo-500" />
                        <Typography variant="h5" fontWeight="bold">Hybrid Agents</Typography>
                        {activeCount > 0 && (
                            <Chip label={`${activeCount} Active`} size="small" color="success"
                                sx={{ height: 20, fontSize: '0.68rem' }} />
                        )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        Deploy lightweight agents inside your network to securely extract, sanitize Protected Data, and upload to S3.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Refresh"><IconButton onClick={refresh} size="small"><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                    <Button variant="contained" startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)} id="btn-register-relay-agent">
                        Register Agent
                    </Button>
                </Stack>
            </Stack>

            {/* ── How it works + Prerequisites ── */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
                <Box sx={{
                    bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200',
                    borderRadius: 2, p: 2.5, flex: 1.4,
                    display: 'flex', gap: 2, alignItems: 'flex-start',
                }}>
                    <DynamicIcon name="Info" className="w-5 h-5 mt-0.5 flex-shrink-0 text-blue-500" />
                    <Box>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>How Hybrid Agents Work</Typography>
                        <Stack spacing={0.75}>
                            {[
                                { step: '1. Register', desc: 'Create an agent here and receive a one-time registration token.' },
                                { step: '2. Deploy', desc: 'Pull the image from AWS ECR Public (public.ecr.aws/f6d2y2r7/aidasapps/relay-agent:latest) and run the Docker container in your on-premise network using the token.' },
                                { step: '3. Connect', desc: 'The agent authenticates with the platform and begins polling for jobs every 30 seconds.' },
                                { step: '4. Extract', desc: 'When a Data Model refresh or Flow execution requires on-premise data, the agent connects to your local database and extracts the requested tables.' },
                                { step: '5. Sanitize', desc: 'Protected Data columns are sanitized on-premise using the configured strategy (mask, hash, suppress, date transform) — raw sensitive data never leaves your network.' },
                                { step: '6. Upload', desc: 'The sanitized data is uploaded as Parquet to S3 via platform-issued pre-signed URLs. No AWS credentials are stored on the agent.' },
                                { step: '7. Audit', desc: 'A compliance audit report is submitted to the platform logging every column sanitized, the strategy used, and row counts.' },
                            ].map((item, i) => (
                                <Typography key={i} variant="caption" color="text.secondary">
                                    <strong>{item.step}:</strong> {item.desc}
                                </Typography>
                            ))}
                        </Stack>
                    </Box>
                </Box>
                <Box sx={{
                    bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200',
                    borderRadius: 2, p: 2.5, minWidth: 300,
                    display: 'flex', gap: 2, alignItems: 'flex-start',
                }}>
                    <DynamicIcon name="ShieldCheck" className="w-5 h-5 mt-0.5 flex-shrink-0 text-green-600" />
                    <Box>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Prerequisites</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>Required:</Typography>
                        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                            {[
                                'Docker Engine (v20.10+) installed on the host machine',
                                'Outbound HTTPS access (port 443) to the AIDAS platform URL',
                                'Network connectivity to your on-premise database (MySQL, PostgreSQL, SQL Server, etc.)',
                                'Sufficient disk space for temporary Parquet file staging (~2x dataset size)',
                            ].map((text, i) => (
                                <Typography key={i} variant="caption" color="text.secondary">✅ {text}</Typography>
                            ))}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>Not Required:</Typography>
                        <Stack spacing={0.5}>
                            {[
                                'No inbound firewall ports — agent initiates all connections outbound',
                                'No AWS credentials — S3 uploads use platform-issued pre-signed URLs',
                                'No database credentials stored — fetched at runtime via Credential Vending API',
                            ].map((text, i) => (
                                <Typography key={i} variant="caption" color="text.secondary">🔒 {text}</Typography>
                            ))}
                        </Stack>
                    </Box>
                </Box>
            </Stack>

            {/* ── Agent Grid ── */}
            {isLoading ? <LoadingSpinner /> : agentList.length === 0 ? (
                <Box sx={{
                    textAlign: 'center', py: 8, bgcolor: 'background.paper', borderRadius: 2,
                    border: '1px dashed', borderColor: 'divider',
                }}>
                    <DynamicIcon name="Server" className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>No agents registered</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Register your first hybrid agent to start extracting on-premise data.
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)} id="btn-register-first-relay-agent">
                        Register an Agent
                    </Button>

                </Box>
            ) : (
                <Grid container spacing={2}>
                    {agentList.map((agent) => (
                        <Grid item xs={12} sm={6} md={4} key={agent.id}>
                            <AgentCard
                                agent={agent} theme={theme}
                                onRevoke={setConfirmRevoke}
                                onDelete={setConfirmDelete}
                                onShowSetup={handleShowSetup}
                                onRegenToken={handleRegenToken}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* ── Dialogs ── */}
            <CreateAgentDialog open={createOpen} onClose={() => setCreateOpen(false)}
                onCreated={handleCreated} token={token} />

            <TokenDialog
                open={Boolean(tokenDialog)}
                agentId={tokenDialog?.agentId}
                agentName={tokenDialog?.regenFor?.name || tokenDialog?.agentName}
                token={tokenDialog?.regToken}
                onClose={() => setTokenDialog(null)}
                onRegenToken={
                    (tokenDialog?.regenFor || tokenDialog?.agentId)
                        ? () => handleRegenToken(tokenDialog.regenFor || { id: tokenDialog.agentId })
                        : null
                }
            />

            <ConfirmModal isOpen={Boolean(confirmRevoke)} onClose={() => setConfirmRevoke(null)}
                onConfirm={handleRevoke}
                title={`Revoke "${confirmRevoke?.name}"?`}
                message="The agent will be blocked from polling for new jobs. Any running jobs will complete." />

            <ConfirmModal isOpen={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}
                onConfirm={handleDelete}
                title={`Delete "${confirmDelete?.name}"?`}
                message="This agent and all its job history will be permanently deleted. This cannot be undone." />

            <ConfirmModal isOpen={Boolean(confirmRegen)} onClose={() => setConfirmRegen(null)}
                onConfirm={executeRegenToken}
                title={`Regenerate token for "${confirmRegen?.name}"?`}
                message="⚠️ This will invalidate the current token, set the agent status to PENDING, and disconnect the running Docker container. The agent must be restarted with the new token." />
        </Box>
    );
};

export default RelayAgentsSettings;
